<template>
    <div class="lazy-parameter" :data-oid="oid">
        <span class="lazy-parameter__na" v-if="(error && [404, 409, 0].indexOf(error.status) >= 0) || resource.value === '-' || resource.value === '-.-'">n/a</span>

        <!-- retry button in case of error -->
        <span class="lazy-parameter__retry-trigger" v-if="error && error.status != 404 && error.status != 409 && error.status != 0">
            <span v-if="error.status == 401">
                <i class="icon icon--locked"></i>
                &nbsp;
            </span>
            <span v-if="error.status == 502">
                <i class="icon icon--offline"></i>
                &nbsp;
            </span>
            <a @click.prevent="retry" class="clickable">
                <i class="icon icon--action-sync"></i>
            </a>
        </span>

        <!-- displays value if present -->
        <span class="lazy-parameter__value" v-if="resource && resource.value !== '-' && resource.value !== '-.-' && !error && !loading">
            <span v-html="resourceLabel"></span>
        </span>

        <edit-popup
            v-if="resource.writeProt === false && uneditable !== true && resource.value !== '-' && resource.value !== '-.-'"
            :loading="loading"
            :restart="restart"
            @edit="edit"
            @save="save"
            @discard="discard"
            class="lazy-parameter__edit-popup"
        >
            <div class="edit-popup__row">
                <span v-if="restart" class="lazy-parameter__editing__warning lazy-parameter__editing__container">
                    <b>{{ $t(`restart.title`) }}</b
                    >: {{ $t(`restart.text`) }}
                </span>
                <span v-if="resourceType === 'enum'" class="lazy-parameter__editing__container">
                    <select v-model="editing.newValue" :disabled="loading" class="form__field form__field--select form__field--short form__field--small">
                        <option :value="key" v-for="(key, index) in editing.enumArray" :key="index">{{ $t(`enums.${oid}.${key}`) }}</option>
                    </select>
                </span>
                <span v-if="resourceType === 'number'" class="lazy-parameter__editing__container">
                    <span v-if="resource.maxValue !== '' && resource.minValue !== ''" class="lazy-parameter__editing__container__description"
                        >Min: {{ resource.minValue }}, Max: {{ resource.maxValue }}</span
                    >
                    <input
                        v-model="editing.newValue"
                        :disabled="loading"
                        type="number"
                        class="form__field form__field--short form__field--small"
                        :max="resource.maxValue"
                        :min="resource.minValue"
                        :step="stepSize * stepResolution"
                    />
                </span>
                <span v-if="resourceType === 'time'" class="lazy-parameter__editing__container">
                    <vue-timepicker v-model="editing.newValue" :disabled="loading" :minute-interval="stepSize * stepResolution" @change="validateTimeInput"></vue-timepicker>
                </span>
                <span v-if="resourceType === 'date'" class="lazy-parameter__editing__container">
                    <datepicker v-model="editing.newValue" name="date_start" input-class="form__field form__field--short" :disabledDates="editing.disabledDates"></datepicker>
                </span>
                <span v-if="resourceType === 'program'" class="lazy-parameter__editing__container lazy-parameter__editing__container--padding-less">
                    <lazy-parameter-program
                        v-if="editing.on"
                        :value="editing.newValue"
                        :min="program.minValue"
                        :max="program.maxValue"
                        :step="program.stepSize"
                        @onValueChange="updateProgramValue"
                    ></lazy-parameter-program>
                </span>
                <span v-if="resourceType === 'program-switch'" class="lazy-parameter__editing__container lazy-parameter__editing__container--padding-less">
                    <lazy-parameter-program
                        v-if="editing.on"
                        :value="editing.newValue"
                        :min="program.minValue"
                        :max="program.maxValue"
                        :switcher="true"
                        @onValueChange="updateProgramValue"
                    ></lazy-parameter-program>
                </span>
                <span v-if="resourceType === 'program-tarif'" class="lazy-parameter__editing__container lazy-parameter__editing__container--padding-less">
                    <lazy-parameter-program
                        v-if="editing.on"
                        :value="editing.newValue"
                        :min="program.minValue"
                        :max="program.maxValue"
                        :tarif="true"
                        @onValueChange="updateProgramValue"
                    ></lazy-parameter-program>
                </span>
                <span v-if="resourceType === 'program-fromto'" class="lazy-parameter__editing__container lazy-parameter__editing__container--padding-less">
                    <lazy-parameter-program
                        v-if="editing.on"
                        :value="editing.newValue"
                        :min="program.minValue"
                        :max="program.maxValue"
                        :fromto="true"
                        @onValueChange="updateProgramFromtoValue"
                    ></lazy-parameter-program>
                </span>
                <span v-if="resourceType === 'program-toggle'" class="lazy-parameter__editing__container lazy-parameter__editing__container--padding-less">
                    <lazy-parameter-program
                        v-if="editing.on"
                        :value="editing.newValue"
                        :min="program.minValue"
                        :max="program.maxValue"
                        :toggle="true"
                        @onValueChange="updateProgramValue"
                    ></lazy-parameter-program>
                </span>
                <span v-if="resourceType === 'reset'" class="lazy-parameter__editing__container">
                    <label>
                        <input
                            v-model="resetInput"
                            @change="resetInput ? (editing.newValue = reset) : (editing.newValue = castedValue())"
                            :disabled="loading == true"
                            class="vertical-align-middle"
                            type="checkbox"
                        />
                        <span class="brick-mobile--dwarf--left vertical-align-middle cursor-pointer">
                            {{ $t('systems.parameters.reset_to') }}:
                            <strong>{{ reset }}</strong>
                        </span>
                    </label>
                </span>
                <span v-if="resourceType === 'text'" class="lazy-parameter__editing__container">
                    <input v-model="editing.newValue" :disabled="loading == true" type="text" class="form__field form__field--short form__field--small" />
                </span>
            </div>
        </edit-popup>

        <!-- loading -->
        <span class="lazy-parameter__loading" v-if="loading">
            <span>{{ $t('common.loading') }}</span
            >...
        </span>
    </div>
</template>

<script>
import * as api from '@/api';
import LazyParameterProgram from '@/components/LazyParameterProgram';
import clickOutside from '@/directives/click-outside';
import VueTimepicker from 'vue2-timepicker';
import Datepicker from 'vuejs-datepicker';
import * as moment from 'moment';
import range from 'lodash/range';
import difference from 'lodash/difference';
import cloneDeep from 'lodash/cloneDeep';
import includes from 'lodash/includes';
import forEach from 'lodash/forEach';
import filter from 'lodash/filter';
import axios from 'axios';
import datapointMixin from '@/mixins/datapoint';
import EditPopup from '@/components/EditPopup';

export default {
    components: {
        LazyParameterProgram,
        VueTimepicker,
        Datepicker,
        EditPopup,
    },
    mixins: [datapointMixin],
    directives: {
        clickOutside,
    },
    props: {
        id: String,
        oid: String,
        fctId: Number,
        node_id: Number,
        active: Boolean,
        uneditable: Boolean,
        condition: Object,
        endpoint: String,
        type: String,
        enum: Array,
        mask: Number,
        bit: Number,
        reset: String,
        omit: Array,
        restart: Boolean,
    },
    data() {
        return {
            resource: false,
            error: false,
            loading: false,
            resetInput: false,
            editing: {
                on: false,
                newValue: null,
            },
            program: {
                minValue: 10,
                maxValue: 30,
                defaultValue: 0,
                stepSize: 1,
            },
            allowSaving: true,
        };
    },
    created() {
        this.defineProgramData();
        this.checkConditions();
        this.$emit('newChild', this._uid);
    },
    computed: {
        resourceType() {
            if (!this.resource) return;
            if (this.type) return this.type;

            return this.getDatapointType(this.resource);
        },
        bitmaskValue() {
            if (!this.resource) return;
            return this.getDatapointBitmaskValue({
                ...this.resource,
                mask: this.mask,
                bit: this.bit,
            });
        },
        unit() {
            if (!this.resource) return;

            return this.getDatapointUnit(this.resource);
        },
        stepSize() {
            return [1, 5, 10, 100, 2, 15][this.resource.stepId];
        },
        stepResolution() {
            switch (this.resource.typeId) {
            case 2:
            case 3:
            case 4:
            case 5:
            case 6:
            case 8:
            case 9:
            case 10:
                return 1;
            case 7:
                return 0.00000012;
            case 11:
                return 1 / 256;
            case 12:
                return 1 / 16;
            case 13:
            case 15:
                return 0.1;
            case 14:
                return 0.01;
            default:
                return 0;
            }
        },
        resourceLabel() {
            if (!this.resource) return '-';
            return this.getDatapointLabel(this.resourceType, this.resource.value, this.oid, this.bitmaskValue, this.unit);
        },
    },
    methods: {
        defineProgramData() {
            switch (this.oid) {
            case '4/82':
                this.program.maxValue = 90;
                this.program.minValue = 0;
                this.program.defaultValue = 50;
                this.program.stepSize = 1;
                break;
            case '5/61':
            case '5/62':
            case '5/63':
            case '5/64':
            case '5/65':
                this.program.maxValue = 75;
                this.program.minValue = 10;
                break;
            case '3/61':
            case '3/62':
            case '3/63':
                this.program.maxValue = 30;
                this.program.minValue = 5;
                break;
            case '58/78':
            case '58/79':
            case '58/80':
                this.program.maxValue = 90;
                this.program.minValue = 0;
                break;
            case '54/15':
                this.program.maxValue = 60;
                this.program.minValue = 0;
                break;
            }
        },
        checkConditions() {
            if (this.active === false) return;

            this.loading = true;

            if (this.condition) {
                if (this.condition.type === 'include-condition') {
                    this.fetchCondition().then(
                        response => {
                            if (!includes(this.condition.values, response.data.value)) {
                                this.loading = false;
                                this.resource = { value: '-' };
                                this.$emit('noChildFound', this._uid);
                            } else {
                                this.fetchData();
                            }
                        },
                        error => {
                            this.loading = false;
                            this.resource = { value: '-' };
                            if ([404, 409, 0].indexOf(error.response.status) >= 0) {
                                this.$emit('noChildFound', this._uid);
                            }
                        }
                    );
                } else {
                    let rejectCounter = 0;
                    let errors = [];

                    forEach(this.condition.conditions, c => {
                        this.fetchCondition().then(
                            response => {
                                console.log(response);
                            },
                            error => {
                                rejectCounter++;
                            }
                        );
                    });

                    if (rejectCounter >= this.condition.conditions.length) {
                        this.loading = false;
                        this.resource = { value: '-' };
                        this.$emit('noChildFound', this._uid);
                    } else {
                        this.fetchData();
                    }
                }
            } else {
                this.fetchData();
            }
        },
        fetchData() {
            const CancelToken = axios.CancelToken;
            const source = CancelToken.source();

            if (this.loading) {
                setTimeout(() => {
                    console.log('timeout');
                    source.cancel('Cancelled');
                }, 15000);
            }

            api.systems
                .datapoint({
                    endpoint: this.endpoint || 'datapoint',
                    id: this.id,
                    node_id: this.node_id,
                    function_id: this.fctId,
                    oid: this.oid,
                    cancelToken: source.token,
                })
                .then(
                    response => {
                        this.loading = false;
                        this.resource = response.data;
                        if (this.resource.value === '-' || this.resource.value === '-.-') {
                            this.$emit('noChildFound', this._uid);
                        }
                    },
                    error => {
                        this.loading = false;
                        if (error.message === 'Cancelled') {
                            this.error = {
                                status: 499,
                            };
                        } else {
                            this.error = error.response;
                            if ([404, 409, 0].indexOf(this.error.status) >= 0) {
                                this.$emit('noChildFound', this._uid);
                            }
                        }
                    }
                )
                .catch(error => {
                    this.error = {
                        status: 499,
                    };
                });
        },
        fetchCondition() {
            return api.systems.datapoint({
                endpoint: this.endpoint || 'datapoint',
                id: this.id,
                node_id: this.node_id,
                function_id: this.fctId,
                oid: this.condition.oid,
            });
        },
        retry() {
            this.error = false;
            this.resource = false;
            this.checkConditions();
        },
        discard() {
            this.editing.on = false;
        },
        edit() {
            setTimeout(() => {
                if (this.uneditable) return;

                if (this.enum) {
                    this.editing.enumArray = this.enum;
                } else if (this.resource.enum) {
                    this.editing.enumArray = JSON.parse(this.resource.enum);
                } else if (this.type == 'enum' && this.resource.minValue && this.resource.maxValue) {
                    this.editing.enumArray = range(parseInt(this.resource.minValue), parseInt(this.resource.maxValue) + 1);
                }

                // filter omitted enum values
                if (this.omit && this.omit.length && this.editing.enumArray && this.editing.enumArray.length) {
                    this.editing.enumArray = difference(this.editing.enumArray, this.omit);
                }

                this.editing.newValue = this.castedValue();

                this.editing.on = true;
            }, 100);
        },
        castedValue() {
            if (this.resourceType == 'number' || this.resourceType == 'enum') {
                return parseFloat(this.resource.value);
            }
            if (this.resourceType == 'program-fromto') {
                return cloneDeep(this.resource.value);
            }
            if (this.resource.unitId == 21) {
                let timeArray = this.resource.value.split(':');
                return this.resource.value == '-' ? { HH: '0', mm: '0' } : { HH: timeArray[0], mm: timeArray[1] };
            }

            if (this.resourceType == 'date') {
                // set disabled dates
                this.editing.disabledDates = {
                    to: moment(this.resource.minValue, 'DD.MM.YYYY').toDate(),
                    from: moment(this.resource.maxValue, 'DD.MM.YYYY').toDate(),
                };
                return moment(this.resource.value, 'DD.MM.YYYY').toDate();
            }

            return cloneDeep(this.resource.value);
        },
        validateTimeInput(eventData) {
            let minTimeArray = this.resource.minValue.split(':');
            let maxTimeArray = this.resource.maxValue.split(':');
            let timeData = eventData.data;
            let h = timeData.HH;
            let m = timeData.mm;
            // check if hours are in time range
            if (parseInt(timeData.h) > parseInt(maxTimeArray[0])) {
                h = maxTimeArray[0];
            } else if (parseInt(timeData.h) < parseInt(minTimeArray[0])) {
                h = minTimeArray[0];
            }
            // check if minutes are in time range
            if (parseInt(timeData.m) > parseInt(maxTimeArray[1])) {
                m = maxTimeArray[1];
            } else if (parseInt(timeData.m) < parseInt(minTimeArray[2])) {
                m = minTimeArray[1];
            }

            this.editing.newValue = `${h}:${m}`;
        },
        validate() {
            if (this.resourceType === 'number' && this.resource.minValue != '' && this.resource.minValue != undefined) {
                if (parseInt(this.editing.newValue) >= parseInt(this.resource.minValue) && parseInt(this.editing.newValue) <= parseInt(this.resource.maxValue)) {
                    return true;
                } else {
                    this.$notify({
                        type: 'error',
                        text: this.$t('systems.parameters.errors.not_in_range'),
                    });
                    return false;
                }
            } else if (typeof this.editing.newValue == 'undefined') {
                this.$notify({
                    type: 'error',
                    text: this.$t('systems.parameters.errors.not_in_range'),
                });
                return false;
            } else {
                return true;
            }
        },
        save() {
            if (!this.validate()) {
                return false;
            }

            this.loading = true;
            this.editing.on = false;

            api.systems
                .datapointUpdate({
                    endpoint: this.endpoint == 'object' ? 'object' : 'datapoint',
                    id: this.id,
                    node_id: this.node_id,
                    function_id: this.fctId,
                    oid: this.oid,
                    value: this.endpoint == 'object' ? this.editing.newValue : this.stringNewValue(),
                })
                .then(
                    response => {
                        this.fetchData();

                        // if(this.restart){
                        //     this.$modal.show(
                        //         () => import('@/views/modal/InfoOnly.vue'),
                        //         {
                        //             title: 'restart.title',
                        //             text: 'restart.text',
                        //         },
                        //         {
                        //             name: 'InfoOnly',
                        //             scrollable: true,
                        //             width: 450,
                        //             height: 'auto',
                        //             class: 'ngdialog-theme-winconnect',
                        //             clickToClose: false,
                        //         }
                        //     );
                        // }
                    },
                    () => {
                        this.loading = false;
                    }
                );
        },
        stringNewValue() {
            switch (this.resource.unitId) {
            case 20:
                return moment(this.editing.newValue).format('DD.MM.YYYY');
            case 21:
                return this.editing.newValue; //moment(this.editing.newValue).format('HH:mm');
            default:
                return '' + this.editing.newValue;
            }
        },
        updateProgramValue(value) {
            this.allowSaving = false;
            forEach(value, v => {
                if (v.switchPoints.length > 0) this.allowSaving = true;
            });

            let fullValue = {
                OID: this.resource.OID,
                subtypeId: this.resource.subtypeId,
                typeId: this.resource.typeId,
                value: cloneDeep(value),
            };
            forEach(fullValue.value, val => {
                forEach(val.switchPoints, point => {
                    point.value = parseInt(point.value);
                });
            });
            this.editing.newValue = fullValue;
        },
        updateProgramFromtoValue(value) {
            this.allowSaving = true;

            let fullValue = {
                OID: this.resource.OID,
                subtypeId: this.resource.subtypeId,
                typeId: this.resource.typeId,
                value: cloneDeep(value),
            };
            this.editing.newValue = fullValue;
        },
    },
};
</script>

<style lang="scss">
.time-picker {
    .dropdown {
        ul {
            li {
                &.active {
                    background: $brand-primary;

                    @media (hover: hover) {
                        &:hover {
                            background: $brand-primary;
                        }
                    }
                }
            }
        }
    }
}

.slide-down-fade-enter,
.slide-down-fade-leave-to {
    opacity: 0;
    transform: translateY(-10px);
}
</style>
